import React from "react";
import { formatDate, formatNumber } from "../format";
import { StatCard, ComparisonBar, vehicleLabel } from "./shared";

const SavingsSummaryCards = ({
  marketPrice,
  spent,
  saved,
  percent,
  translate,
  money,
  marketLabel,
  spentLabel,
  savedLabel,
}) => (
  <>
    <div className="fleetbot-stats">
      <StatCard label={marketLabel} value={money(marketPrice)} />
      <StatCard label={spentLabel} value={money(spent)} />
      <StatCard label={savedLabel} value={money(saved)} accent />
      <StatCard
        label={translate("fleetBotSavingsPercentage")}
        value={`${formatNumber(percent, 1)}%`}
      />
    </div>
    <ComparisonBar
      spent={spent}
      saved={saved}
      spentLabel={translate("fleetBotActualSpent")}
      savedLabel={translate("fleetBotSavedAmount")}
    />
  </>
);

const SavingsNoteCard = ({ item, translate, money }) => (
  <div className="fleetbot-card">
    <div className="fleetbot-card-title fleetbot-card-title-row">
      {item.deliveryNoteNumber}
    </div>
    {item.plateNumber ? (
      <div className="fleetbot-kv">
        <span>{translate("plateNumber")}</span>
        <strong>{item.plateNumber}</strong>
      </div>
    ) : null}
    <div className="fleetbot-kv">
      <span>{translate("deliverynoteDate")}</span>
      <strong>{formatDate(item.serviceDate)}</strong>
    </div>
    <div className="fleetbot-kv">
      <span>{translate("fleetBotMarketPrice")}</span>
      <strong>{money(item.marketPrice)}</strong>
    </div>
    <div className="fleetbot-kv">
      <span>{translate("fleetBotActualSpent")}</span>
      <strong>{money(item.spent)}</strong>
    </div>
    <div className="fleetbot-kv">
      <span>{translate("fleetBotSavedAmount")}</span>
      <strong>{money(item.saved)}</strong>
    </div>
  </div>
);

export function renderSavings(questionId, data, { translate, money }) {
  if (questionId === "totalSavings") {
    const summary = data.summary || {};
    return (
      <>
        <div className="fleetbot-summary-hero">
          <div className="fleetbot-summary-kicker">
            {translate("fleetBotTotalSavings")}
          </div>
          <div className="fleetbot-summary-hero-value">
            {money(summary.totalSaved)}
          </div>
          <div className="fleetbot-summary-hero-label">
            {formatNumber(summary.savingsPercent, 1)}%{" "}
            {translate("fleetBotSavingsPercentage")}
          </div>
        </div>
        <div className="fleetbot-stats" style={{ marginTop: 10 }}>
          <StatCard
            label={translate("fleetBotTotalMarketPrice")}
            value={money(summary.totalMarketPrice)}
          />
          <StatCard
            label={translate("fleetBotTotalSpentAmount")}
            value={money(summary.totalSpent)}
          />
          <StatCard
            label={translate("fleetBotTotalDeliveryNotes")}
            value={formatNumber(summary.totalDeliveryNotes)}
          />
          <StatCard
            label={translate("fleetBotSavedAmount")}
            value={money(summary.totalSaved)}
            accent
          />
        </div>
      </>
    );
  }

  if (questionId === "marketPriceVsSpent") {
    const summary = data.summary || {};
    const list = data.list || [];
    const spentOfMarket =
      summary.totalMarketPrice > 0
        ? Math.min(100, (summary.totalSpent / summary.totalMarketPrice) * 100)
        : 0;

    return (
      <>
        <div className="fleetbot-stats">
          <StatCard
            label={translate("fleetBotMarketPrice")}
            value={money(summary.totalMarketPrice)}
          />
          <StatCard
            label={translate("fleetBotActualSpent")}
            value={money(summary.totalSpent)}
            accent
          />
        </div>
        <div className="fleetbot-meter-row">
          <div className="fleetbot-meter-label">
            <span>{translate("fleetBotMarketPrice")}</span>
            <strong>{money(summary.totalMarketPrice)}</strong>
          </div>
          <div className="fleetbot-meter">
            <span style={{ width: "100%" }} />
          </div>
        </div>
        <div className="fleetbot-meter-row">
          <div className="fleetbot-meter-label">
            <span>{translate("fleetBotActualSpent")}</span>
            <strong>
              {money(summary.totalSpent)} ({formatNumber(spentOfMarket, 1)}%)
            </strong>
          </div>
          <div className="fleetbot-meter">
            <span style={{ width: `${spentOfMarket}%` }} />
          </div>
        </div>
        <div className="fleetbot-section-title">
          {translate("fleetBotMarketPriceVsSpent")}
        </div>
        <div className="fleetbot-list">
          {list.map((item) => {
            const pct =
              item.marketPrice > 0
                ? Math.min(100, (item.spent / item.marketPrice) * 100)
                : 0;
            return (
              <div
                key={item.id || item.deliveryNoteNumber}
                className="fleetbot-card"
              >
                <div className="fleetbot-card-title fleetbot-card-title-row">
                  {item.deliveryNoteNumber}
                </div>
                {item.plateNumber ? (
                  <div className="fleetbot-kv">
                    <span>{translate("plateNumber")}</span>
                    <strong>{item.plateNumber}</strong>
                  </div>
                ) : null}
                <div className="fleetbot-kv">
                  <span>{translate("fleetBotMarketPrice")}</span>
                  <strong>{money(item.marketPrice)}</strong>
                </div>
                <div className="fleetbot-kv">
                  <span>{translate("fleetBotActualSpent")}</span>
                  <strong>{money(item.spent)}</strong>
                </div>
                <div className="fleetbot-meter" style={{ marginTop: 8 }}>
                  <span style={{ width: `${pct}%` }} />
                </div>
                <div className="fleetbot-row-meta">
                  {formatNumber(pct, 1)}% {translate("fleetBotOfMarketPrice")}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  }

  if (questionId === "savingsThisMonth") {
    const monthly = data.monthly || {};
    const items = monthly.items || [];
    return (
      <>
        <div className="fleetbot-section-title">{monthly.month}</div>
        <div className="fleetbot-list">
          {items.map((item) => (
            <SavingsNoteCard
              key={item.id || item.deliveryNoteNumber}
              item={item}
              translate={translate}
              money={money}
            />
          ))}
        </div>
        <div className="fleetbot-section-title">
          {translate("fleetBotSavings")}
        </div>
        <SavingsSummaryCards
          marketPrice={monthly.marketPrice}
          spent={monthly.spent}
          saved={monthly.saved}
          percent={monthly.savingsPercent}
          translate={translate}
          money={money}
          marketLabel={translate("fleetBotMarketPrice")}
          spentLabel={translate("fleetBotActualSpent")}
          savedLabel={translate("fleetBotSavedAmount")}
        />
      </>
    );
  }

  if (questionId === "savingsByVehicle") {
    const list = data.list || [];
    const summary = data.summary || {};
    const max = Math.max(...list.map((item) => Number(item.saved) || 0), 1);
    return (
      <>
        <div className="fleetbot-list">
          {list.map((item) => (
            <div key={item.vehicleId || item.plateNumber} className="fleetbot-card">
              <div className="fleetbot-card-title">{vehicleLabel(item)}</div>
              <div className="fleetbot-kv">
                <span>{translate("fleetBotMarketPrice")}</span>
                <strong>{money(item.marketPrice)}</strong>
              </div>
              <div className="fleetbot-kv">
                <span>{translate("fleetBotActualSpent")}</span>
                <strong>{money(item.spent)}</strong>
              </div>
              <div className="fleetbot-kv">
                <span>{translate("fleetBotSavedAmount")}</span>
                <strong>{money(item.saved)}</strong>
              </div>
              <div className="fleetbot-meter" style={{ marginTop: 8 }}>
                <span style={{ width: `${(item.saved / max) * 100}%` }} />
              </div>
              <div className="fleetbot-row-meta">
                {translate("fleetBotSavingsPercentage")}:{" "}
                {formatNumber(item.savingsPercent, 1)}%
              </div>
            </div>
          ))}
        </div>
        <div className="fleetbot-section-title">
          {translate("fleetBotSavings")}
        </div>
        <SavingsSummaryCards
          marketPrice={summary.totalMarketPrice}
          spent={summary.totalSpent}
          saved={summary.totalSaved}
          percent={summary.savingsPercent}
          translate={translate}
          money={money}
          marketLabel={translate("fleetBotTotalMarketPrice")}
          spentLabel={translate("fleetBotTotalSpentAmount")}
          savedLabel={translate("fleetBotTotalSavedAmount")}
        />
      </>
    );
  }

  return null;
}
